import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import { Box, Text, Flex, Button, Divider, Image } from "@chakra-ui/react";

import Topbar from "components/topbar";
import MainLayout from "components/mainLayout";
import TextInput from "./components/TextInput";
import { useShipmentForm } from "context/DHLShipmentFormContext";
import { useUserContext } from "context/UserContext";
import useGetBags from "api/queries/useGetBags";
import FormikSearchAbleTagSelect from "components/FormikSearchAbleTagSelect";

// 🔑 Separate component that syncs bagIds -> packages
const SyncPackagesWithBags = ({ sentBags }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    const existingPackages = values.packages || [];

    const updatedPackages = values.bagIds.map((bagId, index) => {
      return (
        existingPackages[index] || {
          name: "",
          quantity: 1,
          weight: 5,
          dimensions: { length: 50, width: 50, height: 50 },
        }
      );
    });

    if (JSON.stringify(updatedPackages) !== JSON.stringify(values.packages)) {
      setFieldValue("packages", updatedPackages);
    }
  }, [values.bagIds, values.packages, setFieldValue]);

  return null; // doesn’t render anything, just syncs state
};

const PackageDetails = () => {
  const navigate = useNavigate();
  const { formData, updateFormData } = useShipmentForm();
  const { user } = useUserContext();
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState("");

  const bagsQuery = useGetBags({
    seller_id: user?.info?._id,
    search: search,
    sort: sort,
  });

  const sentBags =
    bagsQuery?.data
      ?.flatMap((request) =>
        request.bags?.filter((bag) => bag.status === "sent")
      )
      ?.filter(Boolean) || [];

  const handleBagSelect = (bagId, formik) => {
    if (!bagId) return;
    const existing = formik.values.bagIds || [];
    if (existing.includes(bagId)) return; // prevent duplicates
    formik.setFieldValue("bagIds", [...existing, bagId]);
  };

  const handleBagRemove = (bagId, formik) => {
    const updated = formik.values.bagIds.filter((id) => id !== bagId);
    formik.setFieldValue("bagIds", updated);
  };

  const validationSchema = Yup.object().shape({
    packages: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required("Packaging is required*"),
          weight: Yup.number()
            .typeError("Weight must be a number")
            .required("Weight is required*"),
          dimensions: Yup.object().shape({
            length: Yup.number()
              .typeError("Length must be a number")
              .required("Length is required*"),
            width: Yup.number()
              .typeError("Width must be a number")
              .required("Width is required*"),
            height: Yup.number()
              .typeError("Height must be a number")
              .required("Height is required*"),
          }),
        })
      )
      .min(1, "At least one package is required*")
      .required("Packages are required*"),
  });

  const initialValues = {
    packages: formData?.contentDetails?.packages || [],
    bagIds: formData?.bagIds || [],
  };
console.log('form data', formData);
  return (
    <MainLayout>
      <Flex minH={"100%"} h={"100%"} bg={"#f8fbff"} flexDirection={"column"}>
        <Topbar title={"SHIP THROUGH DHL"} />
        <Flex
          flex={1}
          direction={"column"}
          justifyContent={"space-between"}
          bg={"#FFFFFF"}
          margin={4}
          padding={6}
          borderRadius={"16px"}
          gap={6}
        >
          <Text
            fontFamily={"PlusJakartaSansSemiBold"}
            fontSize={"24px"}
            lineHeight={"32px"}
            textTransform={"capitalize"}
            color={"#000000"}
          >
            Create Shipment
          </Text>
          <Divider bg={"#8A8F9333"} h={"1px"} />

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              updateFormData({
                contentDetails: { packages: values.packages },
                bagIds: values.bagIds,
              });
              navigate("/send-a-bag/dhl/shipment-date");
            }}
          >
            {(formik) => (
              <Form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  height: "100%",
                }}
              >
                {/* 🔑 Keeps packages in sync with bagIds */}
                <SyncPackagesWithBags sentBags={sentBags} />

                {/* Select Bags */}
                <FormikSearchAbleTagSelect
                  formik={formik}
                  name="bagIds"
                  label="Select Bag IDs"
                  labelColor="black"
                  placeholder="Search Bag IDs..."
                  onSelectChange={(e) => handleBagSelect(e?.value, formik)}
                >
                  {sentBags.map((bag) => (
                    <option key={bag._id} value={bag._id}>
                      {bag.id}
                    </option>
                  ))}
                </FormikSearchAbleTagSelect>

                <Text
                  mt={"20px"}
                  color={"#000000"}
                  fontFamily="PlusJakartaSansSemiBold"
                  fontSize={"16px"}
                  lineHeight={"22px"}
                  textTransform={"capitalize"}
                >
                  Selected Bags IDs
                </Text>

                {/* Selected Bag Chips */}
                <Flex
                  flexWrap="wrap"
                  gap="4px"
                  border="1px solid #D9D9D9"
                  p={4}
                  borderRadius="6px"
                >
                  {formik.values.bagIds.map((id) => {
                    const bag = sentBags?.find((b) => b._id === id);
                    return (
                      <Flex
                        key={id}
                        alignItems="center"
                        gap="4px"
                        minW="80px"
                        bg="#456EFF10"
                        px="16px"
                        py="12px"
                        borderRadius="8px"
                      >
                        <Text
                          color="#456EFF"
                          flex={1}
                          textAlign="center"
                          fontSize="12px"
                        >
                          {bag ? bag.id : id}
                        </Text>
                        <Image
                          src={require("assets/svgs/x-circle.svg").default}
                          cursor="pointer"
                          h="16px"
                          w="16px"
                          onClick={() => handleBagRemove(id, formik)}
                        />
                      </Flex>
                    );
                  })}
                </Flex>

                {/* Package Details */}
                {formik.values.packages.map((pkg, index) => (
                  <Box key={index}>
                    <Flex>
                      <Text
                        fontFamily={"PlusJakartaSansSemiBold"}
                        fontSize={"16px"}
                        lineHeight={"20px"}
                        textTransform={"capitalize"}
                        color={"#000000"}
                        flex={1}
                        mt={"20px"}
                      >
                        Select Packaging - {index + 1}
                      </Text>
                    </Flex>

                    <Flex
                      gap={4}
                      mt={2}
                      borderWidth={"1px"}
                      borderColor={"#D9D9D9"}
                      borderStyle={"solid"}
                      p={4}
                      borderRadius={"6px"}
                      flexDirection={"column"}
                    >
                      <TextInput
                        formik={formik}
                        name={`packages[${index}].name`}
                        label={"Packaging"}
                        placeholder={"Enter Packaging"}
                      
                      />
                      <Flex gap={4}>
                        <TextInput
                          formik={formik}
                          name={`packages[${index}].dimensions.length`}
                          label={"Length"}
                          placeholder={"Enter Length"}
                          unit="cm"
                          type={"number"}
                          disabled
                        />
                        <TextInput
                          formik={formik}
                          name={`packages[${index}].dimensions.width`}
                          label={"Width"}
                          placeholder={"Enter Width"}
                          unit="cm"
                          type={"number"}
                          disabled
                        />
                        <TextInput
                          formik={formik}
                          name={`packages[${index}].dimensions.height`}
                          label={"Height"}
                          placeholder={"Enter Height"}
                          unit="cm"
                          type={"number"}
                          disabled
                        />
                      </Flex>
                      <TextInput
                        formik={formik}
                        name={`packages[${index}].weight`}
                        label={"Weight"}
                        placeholder={"Enter Weight"}
                        unit="kg"
                        type={"number"}
                        disabled
                      />
                    </Flex>
                  </Box>
                ))}

                {/* Footer Buttons */}
                <Flex mt={4}>
                  <Flex>
                    <Button
                      onClick={() => navigate(-1)}
                      width={"148px"}
                      borderRadius={8}
                      bg={"none"}
                      border={"1px solid #000000"}
                      color={"#000000"}
                      fontSize={"12px"}
                      fontWeight={"500"}
                      fontFamily={"PlusJakartaSansMedium"}
                      letterSpacing={0.2}
                    >
                      Back
                    </Button>
                  </Flex>
                  <Flex
                    alignSelf={"flex-end"}
                    justifyContent={"flex-end"}
                    flex={1}
                    bottom={0}
                    w={"100%"}
                    gap={2}
                  >
                    <Button
                      onClick={() => formik.handleSubmit()}
                      flex={1}
                      maxW={"148px"}
                      background={"#000000"}
                      color={"#FFFFFF"}
                      borderRadius={8}
                      fontSize={"12px"}
                      fontFamily={"PlusJakartaSansMedium"}
                      textTransform={"uppercase"}
                    >
                      Next
                    </Button>
                  </Flex>
                </Flex>
              </Form>
            )}
          </Formik>
        </Flex>
      </Flex>
    </MainLayout>
  );
};

export default PackageDetails;
